import type { TDateISO } from "~/@types/TDateISO";

import { type CommitSpaceRebalanceMoveInput, type SpaceMoveInput } from "~/gql/graphqlBase.generated";
import { weeksBetweenIsoWeeks } from "~produce/dashboard/productionCalendar/helpers/calendarWeeks";
import { addIsoWeeks, mondayOfIsoWeek, utcDateToYmd } from "~produce/inventory/hardgoods/helpers/readyWeek";

export interface StagedMove {
	color: string;
	fromIsoWeek: number;
	fromIsoYear: number;
	fromLocationId: string;
	fromLocationName: string;
	fromReadyIsoWeek: number;
	fromReadyIsoYear: number;
	genus: string;
	id: string;
	jobNumber: number;
	lotId: string;
	planName: string;
	productionJobId: string;
	productionPlanId: string;
	series: string;
	toIsoWeek?: number;
	toIsoYear?: number;
	toLocationId?: string;
	toLocationName?: string;
}

export const toWeekStartIso = (isoYear: number, isoWeek: number): string =>
	utcDateToYmd(mondayOfIsoWeek(isoYear, isoWeek));

export const jobLotKey = (productionJobId: string, lotId: string): string => `${productionJobId}|${lotId}`;

export const stagedMoveJobKey = (move: Pick<StagedMove, "lotId" | "productionJobId">): string =>
	jobLotKey(move.productionJobId, move.lotId);

export const formatStagedMoveLabel = (move: Pick<StagedMove, "jobNumber" | "planName">): string =>
	`#${move.jobNumber} ${move.planName}`;

export const hasLocationChange = (move: StagedMove): boolean =>
	move.toLocationId != null && move.toLocationId !== move.fromLocationId;

export const hasWeekChange = (move: StagedMove): boolean =>
	move.toIsoYear != null &&
	move.toIsoWeek != null &&
	(move.toIsoYear !== move.fromIsoYear || move.toIsoWeek !== move.fromIsoWeek);

export const isEffectiveMove = (move: StagedMove): boolean => hasLocationChange(move) || hasWeekChange(move);

export const toPreviewMoveInput = (move: StagedMove): SpaceMoveInput => {
	const { toIsoWeek, toIsoYear, toLocationId } = move;
	return {
		lotId: move.lotId,
		productionJobId: move.productionJobId,
		stagedLocationId: move.fromLocationId,
		stagedWeekStartIso: toWeekStartIso(move.fromIsoYear, move.fromIsoWeek),
		...(toLocationId != null && toLocationId !== move.fromLocationId ? { toLocationId } : {}),
		...(toIsoYear != null && toIsoWeek != null && (toIsoYear !== move.fromIsoYear || toIsoWeek !== move.fromIsoWeek)
			? { toWeekStartIso: toWeekStartIso(toIsoYear, toIsoWeek) }
			: {})
	};
};

export const toPreviewMoveInputs = (moves: StagedMove[]): SpaceMoveInput[] =>
	moves.filter(isEffectiveMove).map(toPreviewMoveInput);

export const toWeekStartDateTime = (isoYear: number, isoWeek: number): TDateISO =>
	mondayOfIsoWeek(isoYear, isoWeek).toISOString() as TDateISO;

const shiftedReadyWeekDateTime = (move: StagedMove, toIsoYear: number, toIsoWeek: number): TDateISO => {
	const weekDelta = weeksBetweenIsoWeeks(
		{ isoWeekNumber: move.fromIsoWeek, isoYear: move.fromIsoYear },
		{ isoWeekNumber: toIsoWeek, isoYear: toIsoYear }
	);
	const ready = addIsoWeeks({ week: move.fromReadyIsoWeek, year: move.fromReadyIsoYear }, weekDelta);
	return toWeekStartDateTime(ready.year, ready.week);
};

export const toCommitMoveInput = (move: StagedMove): CommitSpaceRebalanceMoveInput => {
	const { toIsoWeek, toIsoYear, toLocationId } = move;
	return {
		productionJobId: move.productionJobId,
		productionLotId: move.lotId,
		...(toLocationId != null && toLocationId !== move.fromLocationId ? { toLocationId } : {}),
		...(toIsoYear != null && toIsoWeek != null && (toIsoYear !== move.fromIsoYear || toIsoWeek !== move.fromIsoWeek)
			? {
					toWeekReadyDate: shiftedReadyWeekDateTime(move, toIsoYear, toIsoWeek),
					toWeekStartDate: toWeekStartDateTime(toIsoYear, toIsoWeek)
				}
			: {})
	};
};

export const toCommitMoveInputs = (moves: StagedMove[]): CommitSpaceRebalanceMoveInput[] =>
	moves.filter(isEffectiveMove).map(toCommitMoveInput);

const STORAGE_PREFIX = "sp:staged-moves:";

export interface StagedMovesScope {
	accountEmail: string;
	tenantId: string;
}

export const stagedMovesStorageKey = (scope: StagedMovesScope, siteLocationId: string): string =>
	`${STORAGE_PREFIX}${scope.tenantId}:${scope.accountEmail}:${siteLocationId}`;

const isStagedMove = (value: unknown): value is StagedMove => {
	if (typeof value !== "object" || value === null) return false;
	const move = value as Record<string, unknown>;
	return (
		typeof move.id === "string" &&
		typeof move.productionJobId === "string" &&
		typeof move.lotId === "string" &&
		typeof move.genus === "string" &&
		typeof move.series === "string" &&
		typeof move.color === "string" &&
		typeof move.fromLocationId === "string" &&
		typeof move.fromIsoYear === "number" &&
		typeof move.fromIsoWeek === "number" &&
		typeof move.fromReadyIsoYear === "number" &&
		typeof move.fromReadyIsoWeek === "number"
	);
};

export const loadStagedMoves = (scope: StagedMovesScope, siteLocationId: string): StagedMove[] => {
	if (!siteLocationId) return [];
	try {
		const raw = localStorage.getItem(stagedMovesStorageKey(scope, siteLocationId));
		if (!raw) return [];
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(isStagedMove);
	} catch {
		return [];
	}
};

export const saveStagedMoves = (scope: StagedMovesScope, siteLocationId: string, moves: StagedMove[]): void => {
	if (!siteLocationId) return;
	try {
		localStorage.setItem(stagedMovesStorageKey(scope, siteLocationId), JSON.stringify(moves));
	} catch {
		return;
	}
};
